import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { LDSingleKindContext } from 'launchdarkly-js-client-sdk';
import { EMPTY, catchError, filter, from, map, switchMap, tap } from 'rxjs';

import { DatadogLogsService } from '@ninety/mobile/platform/services';

import { SessionActions } from '../../app-global/session/session.actions';
import { AppSelectors } from '../../app/app.selectors';

import { ANONYMOUS_LD_CONTEXT, LaunchDarklyService } from './api/launch-darkly.service';
import { FeatureFlagActions } from './feature-flag-state.actions';
import { featureFlagSelectors } from './feature-flag-state.selectors';

const dispatchFalse = {
  dispatch: false,
};
@Injectable()
export class FeatureFlagStateEffects {
  private actions$ = inject(Actions);
  private launchDarklyService = inject(LaunchDarklyService);
  private store = inject(Store);
  private logs = inject(DatadogLogsService);

  initLaunchDarkly$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FeatureFlagActions.initLaunchDarkly),
        tap(() => this.launchDarklyService.init(ANONYMOUS_LD_CONTEXT))
      ),
    {
      dispatch: false,
    }
  );

  /**
   * Re-anonymize LD on sign-out. `identify()` on login is never undone, so after an in-app logout the
   * client stays pinned to the signed-out user's identified context; re-pointing it here lets logged-out
   * flag reads resolve (fixes every logged-out flag, not just the launcher).
   */
  reAnonymizeOnSignOut$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.signedOut),
      map(() => FeatureFlagActions.reAnonymizeLaunchDarkly())
    )
  );

  reAnonymizeLaunchDarkly$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FeatureFlagActions.reAnonymizeLaunchDarkly),
        // Flatten identify() so a rejection is caught, not unhandled: a failed re-anonymize leaves
        // flags pinned to the signed-out user (launcher suppressed until restart). Fails safe (EMPTY).
        switchMap(() =>
          from(this.launchDarklyService.updateContext(ANONYMOUS_LD_CONTEXT)).pipe(
            catchError(error => {
              this.logs.error('LD re-anonymize on sign-out failed', undefined, error as Error);
              return EMPTY;
            })
          )
        )
      ),
    dispatchFalse
  );

  userReadyForFlags = createEffect(() =>
    this.store.select(AppSelectors.readyForFlags).pipe(
      filter(ready => !!ready),
      map(() => FeatureFlagActions.updateLaunchDarklyUser())
    )
  );

  closeLaunchDarklyConnection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FeatureFlagActions.closeLaunchDarklyConnection),
        tap(() => this.launchDarklyService.closeConnection())
      ),
    { dispatch: false }
  );

  updateLaunchDarklyUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FeatureFlagActions.updateLaunchDarklyUser),
        concatLatestFrom(() => this.store.select(AppSelectors.userPersonCompany)),
        tap(([_, { user, person, company }]) => {
          if (!user || !person || !company) return;

          const ldUser: LDSingleKindContext = {
            kind: 'user',
            key: user._id,
            anonymous: false,
            email: person?.primaryEmail?.toLowerCase() ?? '',
            userRoleCode: user.roleCode,
            userHasCreatedCompany: person.hasCreatedCompany ?? false,
            userIsImplementer: person.isImplementer ?? false,
            userPartnerType: person.partnerType,

            companyId: company._id,
            companyName: company.name,
            companyImplementerCode: company.implementerCode ?? '',
            companyImplementerFree: company.implementerFree,
            companyAccountStatus: company.accountStatus,
            companyBos: company.bos ?? '',
            companyCreatedByPersonId: company.createdByPersonId,
            companyIndustry: company.industry ?? '',
            companyEmployeeRange: company.employeeRange ?? '',
            //only using this since we just need to see if the company was coached (even though this field is deprecated)
            companyPartnerKey: company.partnerKey ?? '',

            personId: person._id,
            guideEnabled: user.guideEnabled ?? false,
          };
          this.launchDarklyService.updateContext(ldUser);
        })
      ),
    dispatchFalse
  );

  updatedFlagsReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureFlagActions.updatedFlagsReceived),
      concatLatestFrom(() => this.store.select(featureFlagSelectors.selectAll)),
      map(([{ newFlags, context }, oldFlags]) => FeatureFlagActions.processFlagUpdates({ oldFlags, newFlags, context }))
    )
  );

  processFeatureFlagUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FeatureFlagActions.processFlagUpdates),
      map(({ newFlags, oldFlags, context }) => {
        // Use this area to pre-process flags before updating the store
        // Can compare old and new flags here to determine any changes that might need
        // special handling
        return FeatureFlagActions.updatedFlagsProcessed({ flags: newFlags, context });
      })
    )
  );
}
